import { usePortalContainer } from '../PortalContainerContext';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { cn } from '@thinkalpha/ui-core/utils';
import { noop } from 'es-toolkit';
import * as React from 'react';

const TooltipProvider = TooltipPrimitive.Provider;

const TooltipContainer = TooltipPrimitive.Root;

const TooltipTrigger = TooltipPrimitive.Trigger;

const TooltipArrow = TooltipPrimitive.Arrow;

// Context-aware TooltipPortal that uses our portal container context
const TooltipPortal = ({ container, ...props }: React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Portal>) => {
    const contextContainer = usePortalContainer();
    return <TooltipPrimitive.Portal container={container ?? contextContainer} {...props} />;
};

const TooltipContent = React.forwardRef<
    React.ElementRef<typeof TooltipPrimitive.Content>,
    React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 4, children, ...props }, ref) => (
    <TooltipPrimitive.Content
        ref={ref}
        sideOffset={sideOffset}
        className={cn(
            'z-[10000] overflow-hidden rounded-md bg-popover px-3 py-1.5 text-md text-popover-foreground shadow-md animate-in fade-in-0 zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
            className,
        )}
        {...props}
    >
        {children}
        <TooltipArrow className="fill-popover" />
    </TooltipPrimitive.Content>
));
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

/**
 * delayDuration: The duration from when the mouse enters a tooltip trigger until the tooltip opens in ms, default 700.
 */
export type TooltipProps = {
    children: React.ReactNode | string;
    content: React.ReactNode | string;
    className?: string;
    asChild?: boolean;
    /**
     * The duration from when the pointer enters the trigger until the tooltip gets opened.
     * @defaultValue 700
     */
    delayDuration?: number;
    side?: TooltipPrimitive.TooltipContentProps['side'];
    open?: TooltipPrimitive.TooltipProps['open'];
    closeOnContentPointerEnter?: boolean;
    onOpenChange?: TooltipPrimitive.TooltipProps['onOpenChange'];
    onClick?: TooltipPrimitive.TooltipTriggerProps['onClick'];
    onPointerDown?: TooltipPrimitive.TooltipTriggerProps['onPointerDown'];
};

/**
 * A composed tooltip component. covers the 99% use case.
 */
const Tooltip = ({
    asChild,
    children,
    content,
    className,
    delayDuration = 700,
    side,
    open,
    onOpenChange,
    onClick,
    onPointerDown,
    closeOnContentPointerEnter = false,
}: TooltipProps) => {
    const [defaultOpen, setDefaultOpen] = React.useState(open);
    const defaultOnOpenChange = (open: boolean) => {
        if (onOpenChange) {
            onOpenChange(open);
        } else {
            setDefaultOpen(open);
        }
    };
    return (
        <TooltipContainer open={defaultOpen} onOpenChange={defaultOnOpenChange} delayDuration={delayDuration}>
            <TooltipTrigger asChild={asChild} onClick={onClick} onPointerDown={onPointerDown}>
                {children}
            </TooltipTrigger>
            <TooltipPortal>
                <TooltipContent
                    side={side}
                    className={className}
                    onPointerEnter={closeOnContentPointerEnter ? () => defaultOnOpenChange(false) : noop}
                >
                    {content}
                </TooltipContent>
            </TooltipPortal>
        </TooltipContainer>
    );
};

// Export individual components
export { TooltipProvider, TooltipContainer, TooltipTrigger, TooltipContent, TooltipArrow, TooltipPortal };

// Also export the composed Tooltip for backward compatibility
export { Tooltip };
